import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { toast } from "sonner";
import { AlertTriangle, MessageCircle, DollarSign, Clock, Loader2 } from "lucide-react";

/**
 * Dashboard card that surfaces rentals that need to be charged TODAY.
 *
 * Strategy:
 *  - Pulls active rentals via /api/rentals?status=ativo (already enriched with payment_status)
 *  - Filters for `overdue` and `due_soon` (with days_until_due <= 1)
 *  - Sorts by urgency (most overdue first)
 *  - Each row has a "Cobrar via Zelle" button that POSTs /api/billing/zelle-only
 */
export default function CollectionTodayCard() {
  const { t, fmtCurrency } = useT();
  const [items, setItems] = useState(null);
  const [sendingId, setSendingId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/rentals", { params: { status: "ativo" } });
      const rentals = r.data || [];
      const filtered = rentals
        .filter((rt) => {
          const ps = rt.payment_status || {};
          if (ps.status === "overdue") return true;
          if (ps.status === "due_soon" && (ps.days_until_due ?? 99) <= 1) return true;
          return false;
        })
        .sort((a, b) => (a.payment_status?.days_until_due ?? 99) - (b.payment_status?.days_until_due ?? 99));
      setItems(filtered);
    } catch (e) {
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const chargeZelle = async (rentalId) => {
    setSendingId(rentalId);
    try {
      const r = await api.post("/billing/zelle-only", { rental_id: rentalId });
      if (r.data.whatsapp_sent) {
        toast.success(t("collectionToday.sentSuccess"));
      } else {
        toast.success(t("collectionToday.openOnWhatsapp"));
        if (r.data.whatsapp_url) window.open(r.data.whatsapp_url, "_blank");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSendingId(null);
    }
  };

  if (items === null) {
    return (
      <div className="border border-zinc-200 bg-white p-6 flex items-center gap-2 text-zinc-500 text-sm" data-testid="collection-today-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading")}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="border border-zinc-200 bg-emerald-50/40 p-6 flex items-center gap-3" data-testid="collection-today-empty">
        <DollarSign className="w-5 h-5 text-emerald-600" />
        <div>
          <div className="font-display font-bold text-zinc-900">{t("collectionToday.allClear")}</div>
          <div className="text-xs text-zinc-500 font-mono">{t("collectionToday.allClearHint")}</div>
        </div>
      </div>
    );
  }

  const totalDue = items.reduce((acc, r) => acc + (r.payment_status?.balance_due || 0), 0);
  const overdueCount = items.filter((r) => r.payment_status?.status === "overdue").length;

  return (
    <div className="border-2 border-rose-300 bg-white" data-testid="collection-today-card">
      <div className="bg-rose-50 px-5 py-3 border-b border-rose-200 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-700" />
          <div>
            <div className="overline text-rose-700">{t("collectionToday.title")}</div>
            <div className="font-display font-bold text-lg tracking-tight text-zinc-900">
              {t("collectionToday.subtitle", { n: items.length, overdue: overdueCount })}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="overline">{t("collectionToday.totalDue")}</div>
          <div className="font-display font-black text-2xl tabular-nums text-rose-700">
            {fmtCurrency(totalDue)}
          </div>
        </div>
      </div>

      <div className="divide-y divide-zinc-100">
        {items.map((r) => {
          const ps = r.payment_status || {};
          const days = ps.days_until_due;
          const isOverdue = ps.status === "overdue";
          let urgencyLabel = "";
          if (days === 0) urgencyLabel = t("paymentStatus.today");
          else if (days === 1) urgencyLabel = t("paymentStatus.tomorrow");
          else if (days < 0) urgencyLabel = t("paymentStatus.daysLate", { days: Math.abs(days) });

          return (
            <div
              key={r.id}
              className="flex items-center gap-3 px-5 py-3 hover:bg-zinc-50 transition-colors"
              data-testid={`collection-row-${r.id}`}
            >
              <div className={`w-2 h-2 rounded-full ${isOverdue ? "bg-rose-500 animate-pulse" : "bg-amber-500"}`} />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{r.customer?.name || "—"}</div>
                <div className="text-[10px] font-mono text-zinc-500 truncate">
                  {r.car?.model} · {r.car?.plate} · {r.customer?.phone || "—"}
                </div>
              </div>
              <div className="text-right hidden sm:block">
                <div className={`text-[10px] font-mono uppercase ${isOverdue ? "text-rose-700" : "text-amber-700"} flex items-center gap-1 justify-end`}>
                  <Clock className="w-2.5 h-2.5" /> {urgencyLabel}
                </div>
                <div className="font-mono font-bold text-sm tabular-nums">
                  {fmtCurrency(ps.balance_due || 0)}
                </div>
              </div>
              <button
                onClick={() => chargeZelle(r.id)}
                disabled={sendingId === r.id || !r.customer?.phone}
                className="text-[11px] font-display font-bold px-3 py-2 bg-[#7B3FF2] hover:bg-[#6D1FE0] text-white inline-flex items-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`collection-charge-${r.id}`}
                title={!r.customer?.phone ? t("collectionToday.noPhone") : t("collectionToday.chargeZelle")}
              >
                {sendingId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageCircle className="w-3 h-3" />}
                {t("collectionToday.chargeZelle")}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
